import type {
  AdminGame,
  AdminGameVersion,
  ExeReport,
  UpdateGameVersionRequest,
} from '@mcgames/contracts';
import { Badge, Button, Spinner } from '@mcgames/ui';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../../api/hooks.ts';
import { ErrorText } from './ErrorText.tsx';
import { gamePath } from './game-hooks.ts';
import { useVersionMutation } from './version-hooks.ts';
import styles from './Games.module.css';

export function ExeReportsTab({ game }: { game: AdminGame }) {
  const { t } = useTranslation('admin');
  const reports = useApiQuery<ExeReport[]>(`${gamePath(game.id)}/exe-reports`);

  return (
    <>
      <p className={styles['muted']}>{t('games.exeReports.intro')}</p>
      {reports.data !== undefined ? (
        game.versions.length === 0 ? (
          <p className={styles['muted']}>{t('games.exeReports.empty')}</p>
        ) : (
          game.versions.map((version) => (
            <VersionReports
              key={version.id}
              gameId={game.id}
              version={version}
              reports={reports.data.filter((report) => report.versionId === version.id)}
            />
          ))
        )
      ) : reports.isError ? (
        <p role="alert" className={styles['error']}>
          {t('games.exeReports.loadFailed')}
        </p>
      ) : (
        <Spinner />
      )}
    </>
  );
}

type VersionReportsProps = {
  gameId: string;
  version: AdminGameVersion;
  reports: ExeReport[];
};

/** The reports of one version, with that version's current exe. */
function VersionReports({ gameId, version, reports }: VersionReportsProps) {
  const { t } = useTranslation('admin');
  const titleId = useId();
  const adopt = useVersionMutation<Required<Pick<UpdateGameVersionRequest, 'exe'>>>(
    gameId,
    version.id,
    'PATCH',
  );

  return (
    <section aria-labelledby={titleId}>
      <h3 id={titleId} className={styles['subheading']}>
        {version.name}
      </h3>
      <p>
        {version.exe === null
          ? t('games.exeReports.noDefault')
          : t('games.exeReports.currentDefault', { exe: version.exe })}
      </p>
      <ErrorText error={adopt.error} />
      {reports.length === 0 ? (
        <p className={styles['muted']}>{t('games.exeReports.empty')}</p>
      ) : (
        <ReportTable
          versionName={version.name}
          reports={reports}
          currentDefault={version.exe}
          busy={adopt.isPending}
          onAdopt={(exe) => adopt.mutate({ exe })}
        />
      )}
    </section>
  );
}

type ReportTableProps = {
  versionName: string;
  reports: ExeReport[];
  currentDefault: string | null;
  busy: boolean;
  onAdopt: (exePath: string) => void;
};

function ReportTable({ versionName, reports, currentDefault, busy, onAdopt }: ReportTableProps) {
  const { t, i18n } = useTranslation('admin');
  const ids = useId();
  return (
    <div className={styles['tableWrap']}>
      <table
        className={styles['table']}
        aria-label={t('games.exeReports.table', { version: versionName })}
      >
        <thead>
          <tr>
            <th scope="col">{t('games.exeReports.exePath')}</th>
            <th scope="col">{t('games.exeReports.count')}</th>
            <th scope="col">{t('games.exeReports.lastReported')}</th>
            <th scope="col">{t('games.exeReports.action')}</th>
          </tr>
        </thead>
        <tbody>
          {reports.map((report, index) => {
            const pathId = `${ids}-${index}`;
            const isDefault = report.exePath === currentDefault;
            return (
              <tr key={report.exePath}>
                <td id={pathId} className={styles['fileName']}>
                  {report.exePath}
                </td>
                <td>{report.count}</td>
                <td>{new Date(report.lastReportedAt).toLocaleString(i18n.language)}</td>
                <td>
                  {isDefault ? (
                    <Badge tone="accent">{t('games.exeReports.isDefault')}</Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      aria-describedby={pathId}
                      onClick={() => onAdopt(report.exePath)}
                    >
                      {t('games.exeReports.useAsDefault')}
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
